/**
 * Interception Registry
 *
 * Manages all intercepted CSV data, handles deduplication,
 * memory management, and provides lifecycle events.
 */

import type { InterceptedCSV } from '../interceptors/types';
import { EventEmitter } from '../types/events';

/**
 * Registry events
 */
export enum RegistryEvent {
  CSV_ADDED = 'csv:added',
  CSV_REMOVED = 'csv:removed',
  CSV_CLEARED = 'csv:cleared',
  MEMORY_WARNING = 'memory:warning',
}

/**
 * Registry statistics
 */
export interface RegistryStats {
  /** Total number of intercepted CSVs */
  totalCount: number;
  /** Total size in bytes */
  totalSize: number;
  /** Number of unique CSVs (deduplicated) */
  uniqueCount: number;
  /** Number of duplicates detected */
  duplicateCount: number;
  /** Breakdown by interception source */
  bySource: {
    fetch: number;
    xhr: number;
    blob: number;
    download: number;
  };
}

/**
 * LRU Cache entry
 */
interface CacheEntry {
  csv: InterceptedCSV;
  accessCount: number;
  lastAccessed: number;
}

/**
 * Interception Registry class
 */
export class InterceptionRegistry extends EventEmitter {
  private csvs: Map<string, CacheEntry> = new Map();
  private hashIndex: Map<string, string> = new Map(); // hash -> id mapping for deduplication
  private maxSize: number;
  private maxCount: number;
  private duplicateCount: number = 0;

  constructor(maxSize: number = 100 * 1024 * 1024, maxCount: number = 50) {
    super();
    this.maxSize = maxSize; // Default: 100MB
    this.maxCount = maxCount; // Default: 50 CSVs
  }

  /**
   * Add a CSV to the registry
   */
  add(csv: InterceptedCSV): boolean {
    // Check for duplicates by hash
    const existingId = this.hashIndex.get(csv.hash);
    if (existingId && this.csvs.has(existingId)) {
      console.log('[Registry] Duplicate CSV detected, skipping:', {
        id: csv.id,
        existingId,
        hash: csv.hash,
      });
      this.duplicateCount++;

      // Update access time of existing entry
      const existing = this.csvs.get(existingId)!;
      existing.lastAccessed = Date.now();
      existing.accessCount++;

      return false; // Not added (duplicate)
    }

    // Check if we need to evict
    this.enforceMemoryLimits(csv.size);

    // Add to registry
    this.csvs.set(csv.id, {
      csv,
      accessCount: 1,
      lastAccessed: Date.now(),
    });

    // Add to hash index
    this.hashIndex.set(csv.hash, csv.id);

    console.log('[Registry] CSV added:', {
      id: csv.id,
      size: csv.size,
      suggestedName: csv.suggestedName,
      source: csv.interceptedVia,
    });

    // Emit event
    this.emit(RegistryEvent.CSV_ADDED, csv);

    return true; // Successfully added
  }

  /**
   * Get a CSV by ID
   */
  get(id: string): InterceptedCSV | undefined {
    const entry = this.csvs.get(id);
    if (entry) {
      // Update LRU stats
      entry.lastAccessed = Date.now();
      entry.accessCount++;
      return entry.csv;
    }
    return undefined;
  }

  /**
   * Get all CSVs
   */
  getAll(): InterceptedCSV[] {
    return Array.from(this.csvs.values()).map(entry => entry.csv);
  }

  /**
   * Get CSVs sorted by most recently accessed
   */
  getAllSorted(): InterceptedCSV[] {
    return Array.from(this.csvs.values())
      .sort((a, b) => b.lastAccessed - a.lastAccessed)
      .map(entry => entry.csv);
  }

  /**
   * Remove a CSV by ID
   */
  remove(id: string): boolean {
    const entry = this.csvs.get(id);
    if (!entry) {
      return false;
    }

    // Remove from hash index
    this.hashIndex.delete(entry.csv.hash);

    // Remove from registry
    this.csvs.delete(id);

    console.log('[Registry] CSV removed:', { id });

    // Emit event
    this.emit(RegistryEvent.CSV_REMOVED, id);

    return true;
  }

  /**
   * Clear all CSVs
   */
  clear(): void {
    const count = this.csvs.size;

    this.csvs.clear();
    this.hashIndex.clear();
    this.duplicateCount = 0;

    console.log('[Registry] All CSVs cleared:', { count });

    // Emit event
    this.emit(RegistryEvent.CSV_CLEARED);
  }

  /**
   * Get registry statistics
   */
  getStats(): RegistryStats {
    const bySource = {
      fetch: 0,
      xhr: 0,
      blob: 0,
      download: 0,
    };

    let totalSize = 0;

    for (const entry of this.csvs.values()) {
      totalSize += entry.csv.size;
      bySource[entry.csv.interceptedVia]++;
    }

    return {
      totalCount: this.csvs.size + this.duplicateCount,
      totalSize,
      uniqueCount: this.csvs.size,
      duplicateCount: this.duplicateCount,
      bySource,
    };
  }

  /**
   * Check if registry has a CSV with the given hash
   */
  hasByHash(hash: string): boolean {
    return this.hashIndex.has(hash);
  }

  /**
   * Get total size of all CSVs in bytes
   */
  getTotalSize(): number {
    let total = 0;
    for (const entry of this.csvs.values()) {
      total += entry.csv.size;
    }
    return total;
  }

  /**
   * Enforce memory limits using LRU eviction
   */
  private enforceMemoryLimits(newSize: number): void {
    // Check count limit
    while (this.csvs.size >= this.maxCount) {
      this.evictLRU();
    }

    // Check size limit
    while (this.getTotalSize() + newSize > this.maxSize && this.csvs.size > 0) {
      console.warn('[Registry] Memory limit approaching, evicting LRU entries');
      this.emit(RegistryEvent.MEMORY_WARNING, {
        currentSize: this.getTotalSize(),
        maxSize: this.maxSize,
      });
      this.evictLRU();
    }
  }

  /**
   * Evict the least recently used CSV
   */
  private evictLRU(): void {
    if (this.csvs.size === 0) {
      return;
    }

    // Find entry with oldest lastAccessed time
    let oldestId: string | null = null;
    let oldestTime = Infinity;

    for (const [id, entry] of this.csvs.entries()) {
      if (entry.lastAccessed < oldestTime) {
        oldestTime = entry.lastAccessed;
        oldestId = id;
      }
    }

    if (oldestId) {
      console.log('[Registry] Evicting LRU CSV:', { id: oldestId, lastAccessed: oldestTime });
      this.remove(oldestId);
    }
  }

  /**
   * Clean up old CSVs (older than maxAge)
   */
  cleanupOld(maxAge: number = 3600000): number {
    const now = Date.now();
    let cleaned = 0;

    for (const [id, entry] of this.csvs.entries()) {
      if (now - entry.lastAccessed > maxAge) {
        this.remove(id);
        cleaned++;
      }
    }

    if (cleaned > 0) {
      console.log('[Registry] Cleaned up old CSVs:', { count: cleaned, maxAge });
    }

    return cleaned;
  }

  /**
   * Export registry state for persistence
   */
  exportState(): any {
    return {
      csvs: Array.from(this.csvs.entries()).map(([id, entry]) => ({
        id,
        csv: entry.csv,
        accessCount: entry.accessCount,
        lastAccessed: entry.lastAccessed,
      })),
      duplicateCount: this.duplicateCount,
    };
  }

  /**
   * Import registry state from persistence
   */
  importState(state: any): void {
    this.clear();

    if (state.csvs && Array.isArray(state.csvs)) {
      for (const item of state.csvs) {
        this.csvs.set(item.id, {
          csv: item.csv,
          accessCount: item.accessCount,
          lastAccessed: item.lastAccessed,
        });
        this.hashIndex.set(item.csv.hash, item.id);
      }
    }

    if (typeof state.duplicateCount === 'number') {
      this.duplicateCount = state.duplicateCount;
    }

    console.log('[Registry] State imported:', {
      count: this.csvs.size,
      duplicates: this.duplicateCount,
    });
  }

  /**
   * Update memory limits
   */
  setMemoryLimits(maxSize: number, maxCount: number): void {
    this.maxSize = maxSize;
    this.maxCount = maxCount;

    // Enforce new limits
    this.enforceMemoryLimits(0);
  }
}

/**
 * Create a new interception registry
 */
export function createRegistry(maxSize?: number, maxCount?: number): InterceptionRegistry {
  return new InterceptionRegistry(maxSize, maxCount);
}
